import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceArea, BarChart, Bar, LabelList, Cell,
} from "recharts";
import { api } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { useT } from "../i18n";
import { useFmt } from "../i18n/fmt";
import SearchInput from "../components/SearchInput";

type ForecastPoint = { date: string; yhat: number; yhat_low: number; yhat_high: number; model: string };
type HistoryPoint  = { date: string; price: number };
type Driver       = { date: string; event_type: string; intensity: number; snippet?: string | null };
type Backtest     = { model: string; horizon: number; mae: number; rmse: number; mape: number; dir_acc: number; n: number };
type Product = {
  cn_code: string;
  product_name: string;
  history: HistoryPoint[];
  forecast: ForecastPoint[];
  risk: { prob_spike: number; prob_drop: number; prob_anomaly: number };
  opportunity: { score: number; reasons: string[] };
  drivers: Driver[];
  backtest: Backtest[];
};
type Resp = { n_products: number; horizon: number; products: Product[]; data_source?: string };

const EVENT_LABEL_KEYS: Record<string, string> = {
  generic_entry:        "trends.eventGenericEntry",
  patent_expiration:    "trends.eventPatentExpiration",
  regulatory_approval:  "trends.eventRegulatoryApproval",
  regulatory_rejection: "trends.eventRegulatoryRejection",
  safety_alert:         "trends.eventSafetyAlert",
  withdrawal:           "trends.eventWithdrawal",
  shortage:             "trends.eventShortage",
  reimbursement_change: "trends.eventReimbursementChange",
  exclusion:            "trends.eventExclusion",
  merger_acquisition:   "trends.eventMergerAcquisition",
  license_agreement:    "trends.eventLicenseAgreement",
  production_change:    "trends.eventProductionChange",
  logistics_disruption: "trends.eventLogisticsDisruption",
  // Tipos del extractor NLP que faltaban (salían en crudo sobre la gráfica)
  demand_change:              "trends.eventDemandChange",
  geopolitical_supply:        "trends.eventGeopoliticalSupply",
  manufacturer_concentration: "trends.eventManufacturerConcentration",
};

function pct(n: number) { return `${(n * 100).toFixed(0)}%`; }
function eur(n: number) { return `${n.toFixed(2)}€`; }

function RiskBar({ label, value, dot }: { label: string; value: number; dot: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ width: 7, height: 7, borderRadius: 3.5, background: dot, display: "inline-block", flexShrink: 0 }} />
        <span style={{ fontSize: "0.78rem", color: "var(--clr-text3)" }}>{label}</span>
      </div>
      <span style={{ fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", fontSize: "0.85rem" }}>{pct(value)}</span>
    </div>
  );
}

function OppGauge({ score, G }: { score: number; G: string }) {
  const t = useT();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <div style={{ position: "relative", width: 120, height: 120 }}>
        <svg viewBox="0 0 36 36" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
          <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--clr-border)" strokeWidth="2.5" />
          <circle cx="18" cy="18" r="15.9" fill="none" stroke={G} strokeWidth="2.5"
                  strokeDasharray={`${score} 100`} strokeLinecap="round" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)" }}>{score.toFixed(0)}</span>
          <span style={{ fontSize: "0.62rem", color: "var(--clr-text4)", textTransform: "uppercase", letterSpacing: 0.8 }}>{t("trends.opportunity")}</span>
        </div>
      </div>
    </div>
  );
}

export default function TrendsPage() {
  const t = useT();
  const fmt = useFmt();
  const EVENT_LABEL: Record<string, string> = Object.fromEntries(
    Object.entries(EVENT_LABEL_KEYS).map(([k, key]) => [k, t(key)])
  );
  const G = useAuthStore(s => s.user?.primary_color) || "#00843D";
  const [data, setData] = useState<Resp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [horizon, setHorizon] = useState(12);
  const [selectedCn, setSelectedCn] = useState<string | null>(null);
  const [productQuery, setProductQuery] = useState("");

  useEffect(() => {
    setLoading(true); setError("");
    api.get<Resp>(`/predictions/real?horizon=${horizon}&min_points=3`)
      .then(d => { setData(d); setSelectedCn(d.products[0]?.cn_code ?? null); })
      .catch(e => setError(String(e?.message || e)))
      .finally(() => setLoading(false));
  }, [horizon]);

  const selected = useMemo(() =>
    data?.products.find(p => p.cn_code === selectedCn) ?? data?.products[0] ?? null,
  [data, selectedCn]);

  // Selector buscable: con ~960 productos el muro de chips enterraba la gráfica
  // a kilómetro y medio de scroll. Se filtra por nombre/CN y se muestran como
  // mucho MAX_CHIPS chips (el seleccionado siempre, anclado el primero).
  const MAX_CHIPS = 23;
  const productMatches = useMemo(() => {
    if (!data) return { chips: [], hidden: 0 };
    const q = productQuery.trim().toLowerCase();
    const pool = q
      ? data.products.filter(p => p.product_name.toLowerCase().includes(q) || p.cn_code.includes(q))
      : data.products;
    const rest = pool.filter(p => p.cn_code !== selected?.cn_code).slice(0, MAX_CHIPS);
    const chips = selected && (!q || pool.some(p => p.cn_code === selected.cn_code))
      ? [selected, ...rest.filter(p => p.cn_code !== selected.cn_code)]
      : rest;
    return { chips: chips.slice(0, MAX_CHIPS + 1), hidden: Math.max(0, pool.length - chips.length) };
  }, [data, productQuery, selected]);

  const chartData = useMemo(() => {
    if (!selected) return [];
    const hist = selected.history.map(h => ({ date: h.date, hist: h.price }));
    const fc = selected.forecast.map(f => ({
      date: f.date,
      yhat: f.yhat, yhat_low: f.yhat_low, yhat_high: f.yhat_high,
      band: [f.yhat_low, f.yhat_high],
    }));
    return [...hist, ...fc];
  }, [selected]);

  // Eventos sobre la gráfica como ReferenceArea
  const eventBands = useMemo(() => {
    if (!selected) return [];
    return selected.drivers.slice(0, 6).map(d => ({
      x: d.date,
      label: EVENT_LABEL[d.event_type] ?? d.event_type,
    }));
  }, [selected]);

  // Ranking de oportunidades para barra horizontal — solo el top: con ~960
  // productos en 280px el gráfico era papilla ilegible.
  const RANKING_TOP = 15;
  const opportunityRanking = useMemo(() => {
    if (!data) return [];
    return [...data.products]
      .map(p => ({ name: p.product_name.split(" ")[0], score: p.opportunity.score, cn: p.cn_code }))
      .sort((a, b) => b.score - a.score)
      .slice(0, RANKING_TOP);
  }, [data]);

  // Stats globales del dataset
  const stats = useMemo(() => {
    if (!data) return null;
    const histLengths = data.products.map(p => p.history.length);
    const maxHist = histLengths.length ? Math.max(...histLengths) : 0;
    const models: Record<string, number> = {};
    data.products.forEach(p => {
      const m = p.forecast[0]?.model ?? "?";
      models[m] = (models[m] ?? 0) + 1;
    });
    return { maxHist, models };
  }, [data]);

  return (
    <div className="page" style={{ color: "var(--clr-text)", fontVariantNumeric: "tabular-nums" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em" }}>{t("trends.title")}</h1>
          <p style={{ margin: "2px 0 0 0", fontSize: "0.74rem", color: "var(--clr-text4)" }}>{t("trends.disclaimer")}</p>
          <p style={{ margin: "4px 0 0 0", fontSize: "0.82rem", color: "var(--clr-text3)" }}>
            {t("trends.subtitleBase")}
            {data && stats && (
              <>
                {" · "}
                <strong style={{ color: "var(--clr-text2)" }}>{data.products.length}</strong> {t("trends.products")} ·{" "}
                {t("trends.upTo")} <strong style={{ color: "var(--clr-text2)" }}>{(stats.maxHist / 12).toFixed(1)} {t("trends.yearsUnit")}</strong> {t("trends.ofHistory")} ·{" "}
                {Object.entries(stats.models).map(([m, n], i) => (
                  <span key={m}>{i > 0 ? " + " : ""}<strong style={{ color: "var(--clr-text2)" }}>{n}</strong> {m.toUpperCase()}</span>
                ))}
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {[3, 6, 12, 18].map(h => (
            <button key={h} onClick={() => setHorizon(h)} style={{
              background: horizon === h ? G : "var(--clr-surface)",
              color: horizon === h ? "#fff" : "var(--clr-text3)",
              border: horizon === h ? "1px solid transparent" : "1px solid var(--clr-border)",
              borderRadius: 999, padding: "0.42rem 1rem",
              fontSize: "0.78rem", fontWeight: horizon === h ? 600 : 500, cursor: "pointer",
              transition: "all 0.15s",
            }}>{t("trends.horizonMonths", { n: h })}</button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ padding: 60, textAlign: "center" }}>
          <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              border: `3px solid var(--clr-border)`,
              borderTopColor: G,
              animation: "spin 0.9s linear infinite",
            }} />
            <div style={{ fontSize: "0.92rem", color: "var(--clr-text)", fontWeight: 600 }}>
              {t("trends.calculating")}
            </div>
            <div style={{ fontSize: "0.78rem", color: "var(--clr-text4)", maxWidth: 380, lineHeight: 1.5 }}>
              {t("trends.calculatingDetail")}
            </div>
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      {error && <div style={{ padding: 40, textAlign: "center", color: "#ef4444" }}>{error}</div>}

      {data && data.products.length === 0 && !loading && (
        <div style={{ padding: 60, textAlign: "center", background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
          <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--clr-text)", marginBottom: 8 }}>
            {t("trends.noDataTitle")}
          </div>
          <div style={{ fontSize: "0.82rem", color: "var(--clr-text4)", maxWidth: 460, margin: "0 auto", lineHeight: 1.5 }}>
            {t("trends.noDataDetail")}
          </div>
        </div>
      )}

      {data && selected && (
        <>
          {/* Selector de producto: buscador + chips acotados (el seleccionado, anclado) */}
          <div style={{ marginBottom: 18 }}>
            <SearchInput
              value={productQuery}
              onChange={setProductQuery}
              placeholder={t("trends.searchPlaceholder", { n: fmt.num(data.products.length) })}
              minWidth={260}
              style={{
                maxWidth: 420, marginBottom: 10, padding: "0.5rem 0.9rem", borderRadius: 999,
                background: "var(--clr-surface)",
                backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
              }} />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              {productMatches.chips.map(p => (
                <button key={p.cn_code} onClick={() => setSelectedCn(p.cn_code)}
                  style={{
                    background: selected?.cn_code === p.cn_code ? G : "var(--clr-surface)",
                    color: selected?.cn_code === p.cn_code ? "#fff" : "var(--clr-text3)",
                    border: selected?.cn_code === p.cn_code ? "1px solid transparent" : "1px solid var(--clr-border)",
                    borderRadius: 999, padding: "0.42rem 1rem",
                    fontSize: "0.78rem", fontWeight: selected?.cn_code === p.cn_code ? 600 : 500, cursor: "pointer", whiteSpace: "nowrap",
                    transition: "all 0.15s",
                  }}>
                  {p.product_name.length > 28 ? p.product_name.slice(0, 28) + "…" : p.product_name}
                </button>
              ))}
              {productMatches.chips.length === 0 && (
                <span style={{ fontSize: "0.8rem", color: "var(--clr-text4)", padding: "0.42rem 0" }}>
                  {t("trends.noResults", { query: productQuery })}
                </span>
              )}
              {productMatches.hidden > 0 && (
                <span style={{ fontSize: "0.75rem", color: "var(--clr-text4)", whiteSpace: "nowrap" }}>
                  {t("trends.moreHint", { n: fmt.num(productMatches.hidden) })}
                </span>
              )}
            </div>
          </div>

          {/* Layout principal: gráfica grande + panel lateral */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,2.2fr) minmax(280px,1fr)", gap: 20, marginBottom: 20 }}>
            {/* Forecast chart */}
            <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{selected.product_name}</h3>
                <span style={{ fontSize: "0.72rem", color: "var(--clr-text4)" }}>
                  {t("trends.model")}: <strong style={{ color: "var(--clr-text)", fontWeight: 700 }}>{selected.forecast[0]?.model?.toUpperCase() || "—"}</strong>
                </span>
              </div>
              <div style={{ width: "100%", height: 320 }}>
                <ResponsiveContainer>
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"  stopColor={G} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={G} stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--clr-border2)" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={d => d.slice(2, 7)} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} />
                    <YAxis tick={{ fontSize: 10, fill: "var(--clr-text4)" }} tickFormatter={v => `${v}€`} domain={["auto", "auto"]} />
                    <Tooltip
                      contentStyle={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 8, fontSize: "0.78rem" }}
                      formatter={(v: any, n: any) => [typeof v === "number" ? eur(v) : v, n === "hist" ? t("trends.chartHist") : n === "yhat" ? t("trends.chartForecast") : n]}
                    />
                    <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
                    <Area type="monotone" dataKey="band" stroke="none" fill="url(#bandGrad)" name={t("trends.bandInterval")} connectNulls />
                    <Line type="monotone" dataKey="hist" stroke="#475569" strokeWidth={2} dot={false} name={t("trends.histPrice")} connectNulls />
                    <Line type="monotone" dataKey="yhat" stroke={G} strokeWidth={2.5} dot={{ r: 3 }} name={t("trends.forecastLine")} connectNulls strokeDasharray="6 3" />
                    {eventBands.map((e, i) => (
                      <ReferenceArea key={i} x1={e.x} x2={e.x} stroke="#f59e0b" strokeOpacity={0.55} label={{ value: e.label, fontSize: 9, fill: "#a16207", position: "insideTop" }} />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Panel oportunidad + riesgo */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
                <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 700, marginBottom: 10 }}>{t("trends.marketOpportunity")}</h3>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                  <OppGauge score={selected.opportunity.score} G={G} />
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: "0.78rem", color: "var(--clr-text2)", display: "flex", flexDirection: "column", gap: 3 }}>
                  {selected.opportunity.reasons.length === 0
                    ? <li style={{ color: "var(--clr-text4)", listStyle: "none" }}>{t("trends.noStrongSignals")}</li>
                    : selected.opportunity.reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>

              <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
                <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 700, marginBottom: 12 }}>{t("trends.riskOneMonth")}</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <RiskBar label={t("trends.riskSpike")} value={selected.risk.prob_spike} dot="#ef4444" />
                  <RiskBar label={t("trends.riskDrop")} value={selected.risk.prob_drop}  dot="#f59e0b" />
                  <RiskBar label={t("trends.riskAnomaly")}       value={selected.risk.prob_anomaly} dot="#94a3b8" />
                </div>
              </div>
            </div>
          </div>

          {/* Ranking oportunidades + drivers */}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1.4fr) minmax(0,1fr)", gap: 20, marginBottom: 20 }}>
            <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
              <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, marginBottom: 12 }}>{t("trends.opportunityRanking", { n: RANKING_TOP })}</h3>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={opportunityRanking} layout="vertical" margin={{ left: 10, right: 30 }}>
                    <CartesianGrid stroke="var(--clr-border2)" strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "var(--clr-text4)" }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "var(--clr-text2)" }} width={100} />
                    <Tooltip contentStyle={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 8, fontSize: "0.78rem" }} formatter={(v: any) => [`${v}/100`, t("trends.scoreLabel")]} />
                    <Bar dataKey="score" radius={[0, 6, 6, 0]} onClick={(_, idx) => setSelectedCn(opportunityRanking[idx].cn)}>
                      {/* Color por barra en recharts = <Cell>, no <Bar> anidado (aquello pintaba negro) */}
                      {opportunityRanking.map((d, i) => (
                        <Cell key={i} fill={d.score >= 70 ? G : d.score >= 40 ? "#f59e0b" : "#94a3b8"} cursor="pointer" />
                      ))}
                      <LabelList dataKey="score" position="right" style={{ fontSize: 11, fill: "var(--clr-text2)" }} formatter={(v: any) => v.toFixed(0)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
              <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, marginBottom: 12 }}>{t("trends.detectedEvents")}</h3>
              {selected.drivers.length === 0 ? (
                <span style={{ color: "var(--clr-text4)", fontSize: "0.85rem" }}>{t("trends.noEvents")}</span>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", maxHeight: 280, overflowY: "auto" }}>
                  {selected.drivers.map((d, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, padding: "10px 4px", borderTop: i > 0 ? "1px solid var(--clr-border2)" : "none" }}>
                      <span style={{ width: 7, height: 7, borderRadius: 3.5, background: "#f59e0b", display: "inline-block", flexShrink: 0, marginTop: 5 }} />
                      <span style={{ fontSize: "0.7rem", color: "var(--clr-text4)", minWidth: 72 }}>{d.date}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--clr-text)" }}>{EVENT_LABEL[d.event_type] ?? d.event_type}</div>
                        {d.snippet && <div style={{ fontSize: "0.72rem", color: "var(--clr-text3)", marginTop: 2, lineHeight: 1.4 }}>{d.snippet}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Backtest */}
          <div style={{ background: "var(--clr-surface)", border: "1px solid var(--clr-border)", borderRadius: 18, padding: "1.25rem", backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)" }}>
            <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, marginBottom: 12 }}>{t("trends.backtestTitle")}</h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid var(--clr-border2)" }}>
                    {[t("trends.thModel"), t("trends.thHorizon"), "MAE", "RMSE", "MAPE", t("trends.thDirAcc"), "n"].map(h => (
                      <th key={h} style={{ padding: "0.7rem 12px", fontSize: "0.65rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.9, color: "var(--clr-text4)", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selected.backtest.map((b, i) => (
                    <tr key={i} style={{ borderTop: "1px solid var(--clr-border2)" }}>
                      <td style={{ padding: "8px 12px", fontWeight: 600, color: "var(--clr-text)" }}>{b.model.toUpperCase()}</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text2)" }}>{b.horizon} m</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text2)" }}>{b.mae.toFixed(3)}</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text2)" }}>{b.rmse.toFixed(3)}</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text2)" }}>{b.mape.toFixed(1)}%</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text2)" }}>{(b.dir_acc * 100).toFixed(0)}%</td>
                      <td style={{ padding: "8px 12px", color: "var(--clr-text4)" }}>{b.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
